<script setup lang="ts">
import { useI18n } from '@/i18n';
import { findCouponPlanMismatch, type ScopedCoupon } from '@/lib/couponPlans';
import { couponInCurrency, couponLabel } from '@/lib/stripeDiscounts';
import { useBillingStore } from '@/stores/billing';
import type { PlanProduct } from '@/types/billing';
import { useBillingUtils } from '@/use/billingUtils';
import { Alert, Button } from '@dabble/quillui';
import { computed } from 'vue';

const props = defineProps<{
  /** The plan being bought, or the one the customer is on. */
  productId: string | undefined;
  planName: string;
  interval: 'month' | 'year';
  disabled?: boolean;
  /** Render inside another alert (the portal's coupon box) instead of as its own. */
  embedded?: boolean;
  /** Coupons to judge; defaults to the account's plus one waiting for checkout. */
  coupons?: ScopedCoupon[];
}>();
const emit = defineEmits<{ switch: [plan: PlanProduct]; change: []; keep: [] }>();

const { t, locale } = useI18n();
const { couponNameAndTerms } = useBillingUtils();
const billingStore = useBillingStore();

const mismatch = computed(() =>
  findCouponPlanMismatch(
    props.coupons ?? billingStore.scopedCoupons,
    props.productId,
    billingStore.currentOfferingProducts ?? billingStore.offerings?.products,
  ),
);

const subscribedPlan = computed(() => billingStore.primarySubscription?.plan);
const subscribedName = computed(() => subscribedPlan.value?.product?.name || subscribedPlan.value?.name || '');

// A subscriber moving off the plan their coupon covers is told what they give up,
// not steered back to the plan they already have.
const leavingCoveredPlan = computed(() => {
  const id = subscribedPlan.value?.product?.id;
  return Boolean(id && mismatch.value?.coupon.products.includes(id));
});

const switchable = computed(
  () =>
    mismatch.value?.plans.filter((plan) => plan[props.interval]?.id && plan.id !== subscribedPlan.value?.product?.id) ??
    [],
);

const planList = computed(() =>
  new Intl.ListFormat(locale.value, { type: 'disjunction' }).format(switchable.value.map((plan) => plan.name ?? '')),
);

const title = computed(() =>
  leavingCoveredPlan.value
    ? t('billing_coupon_plan_stay_title', { plan: props.planName })
    : switchable.value.length === 1
      ? t('billing_coupon_plan_title', { plan: switchable.value[0].name })
      : t('billing_coupon_plan_title_other'),
);

const body = computed(() =>
  leavingCoveredPlan.value
    ? t('billing_coupon_plan_stay_body', { plan: props.planName, current: subscribedName.value })
    : switchable.value.length
      ? t('billing_coupon_plan_body', { plan: planList.value, current: props.planName })
      : t('billing_coupon_plan_body_other', { current: props.planName }),
);

// A code waiting for checkout that this billing can't take at all: no plan switch fixes that, so say why.
const refusal = computed(() => (props.coupons ? null : billingStore.checkoutCouponRefusal));

// A discount already running on the subscription that can't come along to this billing.
const runningRefusal = computed(() => (props.coupons ? null : billingStore.checkoutDiscountRefusal));
const runningLabel = computed(() => {
  const coupon = runningRefusal.value;
  return coupon
    ? couponNameAndTerms(couponLabel(coupon), couponInCurrency(coupon, billingStore.subscriptionCurrency))
    : '';
});

const intervalNote = computed(() =>
  t(props.interval === 'year' ? 'billing_coupon_plan_keeps_year' : 'billing_coupon_plan_keeps_month'),
);
</script>

<template>
  <Alert
    v-if="refusal || runningRefusal"
    variant="warning"
    :title="refusal ? billingStore.checkoutCoupon?.display : runningLabel"
    class="billing-coupon-plan-notice"
  >
    <p class="billing-coupon-plan-notice__body">
      {{ refusal ? t(`billing_error_${refusal}`) : t(billingStore.checkoutDiscountRefusalKey) }}
    </p>
    <div class="billing-coupon-plan-notice__actions">
      <Button variant="primary" finish="outlined" size="small" :disabled="disabled" @click="emit('change')">
        {{ t('billing_change_plan') }}
      </Button>
    </div>
  </Alert>
  <component
    :is="embedded ? 'div' : Alert"
    v-else-if="mismatch"
    :variant="embedded ? undefined : 'warning'"
    :title="embedded ? undefined : title"
    class="billing-coupon-plan-notice"
    :class="{ 'billing-coupon-plan-notice--embedded': embedded }"
  >
    <strong v-if="embedded" class="billing-coupon-plan-notice__title">{{ title }}</strong>
    <p class="billing-coupon-plan-notice__body">{{ body }}</p>
    <div class="billing-coupon-plan-notice__actions">
      <Button
        v-if="leavingCoveredPlan"
        variant="primary"
        finish="outlined"
        size="small"
        :disabled="disabled"
        @click="emit('keep')"
      >
        {{ t('billing_coupon_plan_keep', { plan: subscribedName }) }}
      </Button>
      <template v-if="switchable.length">
        <Button
          v-for="plan in switchable"
          :key="plan.id"
          variant="primary"
          size="small"
          :disabled="disabled"
          @click="emit('switch', plan)"
        >
          {{ t('billing_coupon_plan_switch', { plan: plan.name }) }}
        </Button>
        <span class="billing-coupon-plan-notice__hint">{{ intervalNote }}</span>
      </template>
      <Button
        v-else-if="!leavingCoveredPlan"
        variant="primary"
        finish="outlined"
        size="small"
        :disabled="disabled"
        @click="emit('change')"
      >
        {{ t('billing_change_plan') }}
      </Button>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.billing-coupon-plan-notice {
  &--embedded {
    margin-top: space('small');
    padding-top: space('small');
    border-top: 1px solid theme('color-border-secondary');
  }

  &__title {
    display: block;
    margin-bottom: space('xxsmall');
  }

  &__body {
    margin: 0;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: space('small');
    margin-top: space('small');
  }

  &__hint {
    font-size: fontSize('small');
    color: theme('color-text-secondary');
  }
}
</style>
